import { toJalaliFullDate } from "../../../middleware/middleware";
import { estimateRequestIncome } from "./estimatedIncome";
import { estimateCommissioning } from "./estimateCommissioning";
import { buildLoanSummary } from "./requestLoanSummary";

export function resolveRequestReferenceId(request) {
  if (!request) return null;

  const candidates = [
    request.referenceID,
    request.ReferenceID,
    request.referenceId,
    request.ReferenceId,
  ];

  for (const value of candidates) {
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && numeric > 0) return numeric;
  }

  const requestId = Number(request.id ?? request.Id);
  if (!Number.isNaN(requestId) && requestId > 0) return requestId + 14000000;

  return null;
}

export function formatRequestCode(referenceId) {
  const resolved =
    typeof referenceId === "object" && referenceId !== null
      ? resolveRequestReferenceId(referenceId)
      : Number(referenceId);

  if (resolved == null || Number.isNaN(resolved) || resolved <= 0) return "—";
  return String(resolved);
}

export function buildRequestTitle(request) {
  const capacity = request?.plantCapacityTitle;
  const type = request?.powerPlantTypeTitle;
  if (capacity && type) return `درخواست نیروگاه ${capacity} ${type}`;
  if (capacity) return `درخواست نیروگاه ${capacity}`;
  return request?.projectTitle || "درخواست نیروگاه خورشیدی";
}

function formatRelativeUpdate(dateValue) {
  if (!dateValue) return "ثبت نشده";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return toJalaliFullDate(dateValue) || "ثبت نشده";

  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "کمتر از یک ساعت پیش";
  if (diffHours < 24) return `${diffHours.toLocaleString("fa-IR")} ساعت پیش`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays.toLocaleString("fa-IR")} روز پیش`;
  return toJalaliFullDate(dateValue);
}

function defaultStageDescription(step) {
  if (!step?.stepTitle) {
    return "پرونده شما در حال پیگیری است. در صورت نیاز به اقدام، در بخش «اقدامات شما» اطلاع‌رسانی می‌شود.";
  }
  return `در مرحله «${step.stepTitle}» هستید. جزئیات و اقدامات لازم در همین صفحه نمایش داده می‌شود.`;
}

function getStatusPresentation(request) {
  const confirm = request?.requestConfirm || request?.RequestConfirm;
  switch (confirm) {
    case "Canceled":
      return { chipColor: "error", progressColor: "error" };
    case "Completed":
      return { chipColor: "success", progressColor: "success" };
    case "InProcess":
      return { chipColor: "primary", progressColor: "success" };
    case "NotChecked":
      return { chipColor: "default", progressColor: "primary" };
    default:
      return { chipColor: "warning", progressColor: "primary" };
  }
}

function resolveRequestStatusLabel(request, useWorkflowMilestones = false) {
  const title = request?.requestConfirmTitle || request?.RequestConfirmTitle;
  if (title) return title;

  switch (request?.requestConfirm || request?.RequestConfirm) {
    case "Canceled":
      return "لغو شده";
    case "Completed":
      return "تکمیل شده";
    case "InProcess":
      return "در حال پیگیری";
    case "NotChecked":
      return "در انتظار بررسی";
    default:
      return useWorkflowMilestones ? null : "در حال پیگیری";
  }
}

/** زمان‌بندی + KPI بهره‌برداری */
function buildSchedule(request, currentStageTitle, activeIndex, totalSteps) {
  const confirm = request?.requestConfirm;
  const currentStage = currentStageTitle || "—";
  const commissioning = estimateCommissioning(request, activeIndex, totalSteps);

  const stageCompletionLabel =
    confirm === "InProcess"
      ? "۳ تا ۷ روز کاری"
      : confirm === "Completed"
        ? "تکمیل شده"
        : confirm === "Canceled"
          ? "—"
          : "پس از تأیید کارشناس";

  const commissioningRangeLabel =
    confirm === "Canceled"
      ? "—"
      : confirm === "Completed" && commissioning.dateLabel
        ? commissioning.dateLabel
        : "۳۰ تا ۴۵ روز کاری";

  if (confirm === "Canceled") {
    return {
      currentStage,
      stageCompletionLabel,
      commissioningRangeLabel,
      showEstimates: false,
      commissioning,
      scheduleNote:
        "این درخواست لغو شده است. زمان تکمیل مرحله و بهره‌برداری قابل نمایش نیست.",
    };
  }

  if (confirm === "Completed") {
    return {
      currentStage,
      stageCompletionLabel,
      commissioningRangeLabel,
      showEstimates: false,
      commissioning,
      scheduleNote: null,
    };
  }

  if (confirm !== "InProcess") {
    return {
      currentStage,
      stageCompletionLabel,
      commissioningRangeLabel,
      showEstimates: false,
      commissioning,
      scheduleNote:
        "زمان‌بندی دقیق پس از شروع رسمی بررسی پرونده توسط کارشناس اعلام می‌شود.",
    };
  }

  return {
    currentStage,
    stageCompletionLabel,
    commissioningRangeLabel,
    showEstimates: true,
    commissioning,
    scheduleNote: commissioning.isEstimate
      ? "تاریخ بهره‌برداری بر اساس پیشرفت فعلی تخمین زده شده و پس از اعلام کارشناس به‌روز می‌شود."
      : null,
  };
}

function isActionableFormItem(item) {
  if (!item || item.isVisible === false || item.isReadOnly) return false;
  if (!item.label || item.label.trim() === "" || item.label === "FormItem") return false;
  if (!item.key) return false;

  if (item.type === "upload" || item.type === "file") return true;
  if (item.type === "button" || item.key === "contractorSelector" || item.key === "paymentSelector") {
    return true;
  }
  if (item.type === "checkbox" && item.isRequired) return true;
  return false;
}

function mapFormItemToTask(item) {
  const isUpload = item.type === "upload" || item.type === "file";
  const isSelector =
    item.key === "contractorSelector" || item.key === "paymentSelector" || item.type === "button";

  return {
    id: item.key,
    title: item.label,
    description: item.isRequiredText || item.description || (isUpload ? "لطفاً فایل را بارگذاری کنید." : "لطفاً این مرحله را تکمیل کنید."),
    urgency: item.isRequired ? "urgent" : "waiting",
    urgencyLabel: item.isRequired ? "فوری" : "در انتظار شما",
    actionLabel: isSelector ? "انجام اقدام" : isUpload ? "بارگذاری" : "تکمیل",
    fieldKey: item.key,
    type: item.type,
  };
}

export function extractCustomerTasks(step) {
  if (!step?.stepFormData?.isShowModifyForm) return [];

  const items = step.stepFormData?.formItems || [];
  return items.filter(isActionableFormItem).map(mapFormItemToTask);
}

function isStepVisibleToCustomer(step) {
  return step?.showToCustomer === true || step?.ShowToCustomer === true;
}

const CUSTOMER_WORKFLOW_MILESTONE_TEMPLATES = [
  {
    code: "RegistrationReview",
    title: "ثبت و بررسی درخواست",
    description: "درخواست شما ثبت شده و توسط کارشناس سولارنت در حال بررسی است.",
  },
  {
    code: "SiteVisit",
    title: "بازدید محل",
    description: "بازدید فنی از محل نیروگاه برای ارزیابی شرایط اجرا انجام می‌شود.",
  },
  {
    code: "CustomerContract",
    title: "قرارداد",
    description: "قرارداد را بررسی، دانلود، امضا و در سامانه بارگذاری می‌کنید.",
  },
  {
    code: "FinalApproval",
    title: "تایید نهایی",
    description: "پس از تکمیل قرارداد، تایید نهایی پرونده توسط سولارنت انجام می‌شود.",
  },
  {
    code: "ProjectExecution",
    title: "اجرای پروژه",
    description: "احداث و اجرای نیروگاه در محل شما انجام می‌شود.",
  },
  {
    code: "TestAndDelivery",
    title: "تست و تحویل",
    description: "تست نیروگاه و تحویل پروژه انجام می‌شود.",
  },
  {
    code: "Commissioning",
    title: "بهره‌برداری",
    description: "صدور پروانه بهره‌برداری و شروع بهره‌برداری نیروگاه.",
  },
];

function normalizeLegacyStepKey(step) {
  const raw =
    step?.powerPlantProjectStep ??
    step?.PowerPlantProjectStep ??
    step?.stepTitle ??
    "";
  if (typeof raw === "number") return raw;
  const text = String(raw).trim();
  const enumMap = {
    PlantRegistrationByUser: 1,
    VerifyRegistrationBySolarent: 2,
    VisitSchedulingBySupervisor: 3,
    VisitFormCompletionBySupervisor: 4,
    MehrsunContract: 7,
    ContractorSelectionByUser: 8,
    SolarentContract: 9,
    ContractBetweenUserAndContractor: 10,
    ProjectPayment: 11,
    ProjectPaymentVerifyBySolarent: 12,
    ScheduleSubmissionByContractor: 13,
    DesignByContractor: 14,
    DesignReviewBySupervisor: 15,
    ConstructionByContractor: 16,
    ConstructionReviewBySupervisor: 17,
    TestingByContractor: 18,
    GridConnectionBySupervisor: 20,
    OperationLicenseIssuance: 21,
    Done: 22,
  };
  if (enumMap[text] != null) return enumMap[text];
  const parsed = Number(text);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function mapLegacyStepToMilestoneIndex(step, request) {
  if (request?.requestConfirm === "Completed") {
    return CUSTOMER_WORKFLOW_MILESTONE_TEMPLATES.length - 1;
  }

  const legacyStep = normalizeLegacyStepKey(step);
  switch (legacyStep) {
    case 1:
    case 2:
      return 0;
    case 3:
    case 4:
    case 7:
      return 1;
    case 9:
      return 2;
    case 11:
    case 12:
      return 2;
    case 8:
    case 10:
    case 13:
    case 14:
    case 15:
    case 16:
    case 17:
      return 4;
    case 18:
      return 5;
    case 20:
    case 21:
    case 22:
      return 6;
    default:
      return 0;
  }
}

function buildFallbackTrackingMilestones(stepList, request) {
  const inProcessStep =
    stepList.find((s) => s.requestConfirm === "InProcess") ||
    stepList.find((s) => s.stepInfoId === request?.stepInfoId) ||
    stepList[0];

  const activeIndex = mapLegacyStepToMilestoneIndex(inProcessStep, request);

  return CUSTOMER_WORKFLOW_MILESTONE_TEMPLATES.map((template, index) => ({
    code: template.code,
    title: template.title,
    description: template.description,
    order: index + 1,
    status:
      request?.requestConfirm === "Completed"
        ? "completed"
        : index < activeIndex
          ? "completed"
          : index === activeIndex
            ? "current"
            : "future",
  }));
}

function isGridConnectedPlant(request) {
  const raw = request?.powerPlantType ?? request?.PowerPlantType ?? "";
  const text = String(raw).trim();
  return text === "1" || text === "GridConnected" || text.includes("متصل");
}

function buildFallbackParallelMilestones(stepList, request) {
  if (!isGridConnectedPlant(request) && request?.needsLoan !== true && request?.NeedsLoan !== true) {
    return [];
  }

  const inProcessStep =
    stepList.find((s) => s.requestConfirm === "InProcess") ||
    stepList.find((s) => s.stepInfoId === request?.stepInfoId) ||
    stepList[0];
  const mainActiveIndex = mapLegacyStepToMilestoneIndex(inProcessStep, request);
  const legacyStep = normalizeLegacyStepKey(inProcessStep);
  const visitPhaseDone = mainActiveIndex > 1 || legacyStep === 7;
  const parallelStatus =
    visitPhaseDone || (mainActiveIndex >= 1 && (legacyStep === 7 || mainActiveIndex > 1))
      ? "current"
      : "future";
  const result = [];

  if (isGridConnectedPlant(request)) {
    result.push({
      code: "MehrsanContract",
      title: "مهرسان",
      description: "پیگیری قرارداد خرید برق با مهرسان.",
      order: 1,
      status: legacyStep === 7 ? "current" : parallelStatus,
      track: "parallel",
      lane: "mehrsan",
      anchorOrder: 2,
      statusTitle: request?.mehrsanSummaryTitle || request?.MehrsanSummaryTitle || null,
    });
  }

  if (
    request?.needsLoan === true ||
    request?.NeedsLoan === true ||
    isGridConnectedPlant(request)
  ) {
    result.push({
      code: "LoanProcess",
      title: "وام",
      description: "پیگیری درخواست وام.",
      order: 2,
      status: parallelStatus,
      track: "parallel",
      lane: "loan",
      anchorOrder: 2,
      statusTitle: request?.lendingSummaryTitle || request?.LendingSummaryTitle || null,
    });
  }

  return result;
}

function splitTrackingMilestones(milestones) {
  const all = milestones || [];
  const main = all.filter(
    (m) => (m.track || m.Track || "main").toLowerCase() !== "parallel"
  );
  const parallel = all.filter(
    (m) => (m.track || m.Track || "").toLowerCase() === "parallel"
  );
  return {
    main: main.length ? main : all,
    parallel,
  };
}

function mapMilestoneToTimelineStep(milestone, index) {
  const statusTitle = milestone.statusTitle || milestone.StatusTitle;
  const baseDescription = milestone.description || milestone.Description || "";
  return {
    stepInfoId: `v2-${milestone.code || milestone.Code || index}`,
    stepTitle: milestone.title || milestone.Title || "—",
    stepDescription: statusTitle
      ? `${baseDescription} (${statusTitle})`
      : baseDescription,
    requestConfirm: mapMilestoneStatusToRequestConfirm(
      milestone.status || milestone.Status
    ),
    isWorkflowMilestone: true,
    track: milestone.track || milestone.Track || "main",
    lane: milestone.lane || milestone.Lane,
    anchorOrder: milestone.anchorOrder || milestone.AnchorOrder || 2,
    statusTitle,
  };
}

const HIDDEN_CUSTOMER_MILESTONE_CODES = new Set([
  "PriceOffer",
  "PriceAcceptance",
  "CustomerPriceAcceptance",
]);

function isHiddenCustomerMilestone(milestone) {
  const code = String(milestone?.code || milestone?.Code || "").trim();
  const title = String(milestone?.title || milestone?.Title || "").trim();
  if (HIDDEN_CUSTOMER_MILESTONE_CODES.has(code)) return true;
  return title === "پیشنهاد قیمت" || title === "پذیرش قیمت" || title === "پیشنهاد";
}

function normalizeApiTrackingMilestones(milestones) {
  if (!milestones?.length) return milestones;
  const main = milestones.filter(
    (m) =>
      (m.track || m.Track || "main").toLowerCase() !== "parallel" &&
      !isHiddenCustomerMilestone(m)
  );
  const parallel = milestones.filter(
    (m) => (m.track || m.Track || "").toLowerCase() === "parallel"
  );
  return main.length ? [...main, ...parallel] : milestones;
}

function resolveTrackingMilestones(stepList, request, trackingMilestones) {
  if (trackingMilestones?.length) {
    const hadHiddenMilestones = trackingMilestones.some(isHiddenCustomerMilestone);
    const normalized = normalizeApiTrackingMilestones(trackingMilestones);
    const main = (normalized || []).filter(
      (m) => (m.track || m.Track || "main").toLowerCase() !== "parallel"
    );
    const hasPostExecutionStages = main.some((m) => {
      const code = String(m.code || m.Code || "").trim();
      return code === "TestAndDelivery" || code === "Commissioning";
    });

    if (
      !hadHiddenMilestones &&
      main.length >= CUSTOMER_WORKFLOW_MILESTONE_TEMPLATES.length &&
      hasPostExecutionStages
    ) {
      return normalized;
    }
  }
  return buildFallbackTrackingMilestones(stepList, request);
}

function resolveParallelTrackingMilestones(
  stepList,
  request,
  parallelTrackingMilestones,
  allTrackingMilestones
) {
  if (parallelTrackingMilestones?.length) return parallelTrackingMilestones;
  const fromAll = (allTrackingMilestones || []).filter(
    (m) => (m.track || m.Track || "").toLowerCase() === "parallel"
  );
  if (fromAll.length) return fromAll;
  return buildFallbackParallelMilestones(stepList, request);
}

function mapMilestoneStatusToRequestConfirm(status) {
  if (status === "completed") return "Completed";
  if (status === "current") return "InProcess";
  return "NotChecked";
}

export function buildCustomerTrackingModel({
  request,
  stepList = [],
  trackingMilestones = [],
  parallelTrackingMilestones = [],
  parallelInsertAfterMainIndex = 1,
  activeTrackingMilestoneIndex = 0,
  activeStepIndex = 0,
  shownStepIndex = 0,
}) {
  const allSteps = stepList || [];
  const resolvedMilestones = resolveTrackingMilestones(
    allSteps,
    request,
    trackingMilestones
  );
  const { main: mainMilestones } = splitTrackingMilestones(resolvedMilestones);
  const parallelMilestones = resolveParallelTrackingMilestones(
    allSteps,
    request,
    parallelTrackingMilestones,
    resolvedMilestones
  );
  const useWorkflowMilestones = mainMilestones.length > 0;

  const visibleSteps = allSteps.filter((s) => isStepVisibleToCustomer(s));
  const legacyTimelineSteps = visibleSteps.length ? visibleSteps : allSteps;

  const timelineSteps = useWorkflowMilestones
    ? mainMilestones.map((milestone, index) => mapMilestoneToTimelineStep(milestone, index))
    : legacyTimelineSteps;

  const parallelTimelineSteps = useWorkflowMilestones
    ? parallelMilestones.map((milestone, index) => mapMilestoneToTimelineStep(milestone, index))
    : [];

  const currentStepById = allSteps.findIndex(
    (s) => s.stepInfoId === request?.stepInfoId
  );

  const activeTimelineIndex = useWorkflowMilestones
    ? Math.min(
        Math.max(activeTrackingMilestoneIndex ?? 0, 0),
        Math.max(timelineSteps.length - 1, 0)
      )
    : currentStepById >= 0
      ? timelineSteps.findIndex((s) => s.stepInfoId === request?.stepInfoId)
      : Math.min(activeStepIndex, Math.max(0, timelineSteps.length - 1));

  const safeActiveIndex =
    activeTimelineIndex >= 0 ? activeTimelineIndex : 0;
  const total = timelineSteps.length || 1;
  const progressPercent =
    total <= 1
      ? request?.requestConfirm === "Completed"
        ? 100
        : 0
      : Math.min(
          100,
          Math.max(0, Math.round((safeActiveIndex / (total - 1)) * 100))
        );

  const currentStep =
    allSteps[shownStepIndex] ||
    allSteps[activeStepIndex] ||
    timelineSteps[safeActiveIndex];

  const statusPresentation = getStatusPresentation(request);
  const statusLabel = resolveRequestStatusLabel(request, useWorkflowMilestones);

  const currentStageTitle = useWorkflowMilestones
    ? timelineSteps[safeActiveIndex]?.stepTitle ||
      request?.workflowV2StageTitle ||
      request?.WorkflowV2StageTitle ||
      CUSTOMER_WORKFLOW_MILESTONE_TEMPLATES[safeActiveIndex]?.title ||
      "ثبت و بررسی درخواست"
    : currentStep?.stepTitle || request?.stepTitle || statusLabel || "در حال پیگیری";

  const showRequestStatusChip = Boolean(
    statusLabel &&
      statusLabel.trim() !== (currentStageTitle || "").trim()
  );
  const commissioning = estimateCommissioning(request, safeActiveIndex, total);
  const customerTasks = extractCustomerTasks(currentStep);
  const primaryTask = customerTasks[0] || null;

  return {
    requestTitle: buildRequestTitle(request),
    plantTypeLabel: request?.powerPlantTypeTitle || "—",
    plantCapacityLabel: request?.plantCapacityTitle || "—",
    statusLabel: statusLabel || "—",
    showRequestStatusChip,
    statusChipColor: statusPresentation.chipColor,
    progressColor: statusPresentation.progressColor,
    currentStageTitle,
    currentStageDescription: useWorkflowMilestones
      ? timelineSteps[safeActiveIndex]?.stepDescription ||
        request?.workflowV2NextActionTitle ||
        request?.WorkflowV2NextActionTitle ||
        defaultStageDescription(currentStep)
      : currentStep?.adminComment ||
        currentStep?.description ||
        defaultStageDescription(currentStep),
    progressPercent,
    expertName:
      request?.assignedOperatorFullName ||
      request?.AssignedOperatorFullName ||
      "اختصاص یافته به کارشناس",
    expertPhone: request?.mobile || request?.telephon || null,
    lastUpdateLabel: formatRelativeUpdate(
      request?.updateOn || request?.lastFollowUpDate
    ),
    requestCode: formatRequestCode(request),
    requestDateLabel: toJalaliFullDate(request?.createOn) || "—",
    timelineSteps,
    parallelTimelineSteps,
    parallelInsertAfterMainIndex: parallelInsertAfterMainIndex ?? 1,
    activeTimelineIndex: safeActiveIndex,
    shownStepIndex,
    schedule: buildSchedule(
      request,
      currentStep?.stepTitle,
      safeActiveIndex,
      total
    ),
    commissioning,
    loanSummary: buildLoanSummary(request),
    timeRemainingLabel: commissioning.timeRemainingLabel,
    primaryTask,
    income: estimateRequestIncome(request),
    customerTasks,
    showActionCenter:
      request?.requestConfirm === "InProcess" && customerTasks.length > 0,
    currentStep,
  };
}
